import { useCallback, useMemo, useState } from 'react';
import startCase from 'lodash/startCase';
import { COLORS } from '../../constant/colors';
import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Divider,
  Input,
  InputGroup,
  InputRightElement,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import { ReliusSequenceIdWarningModal } from './ReliusSequenceIdWarningModal';
import { PensionProConfiguration, ReliusConfigurationSchema } from '../types';
import { LockIcon } from '../../shared/icons/LockIcon';
import { useUpdateReliusConfiguration } from '../hooks/useUpdateReliusConfiguration';
import { useConnectionConfiguration } from '../hooks/useConnectionConfiguration';
import { useReliusSequenceIds } from '../hooks/useGetReliusConfiguration';
import { useParams } from 'react-router-dom';
import { useUpdatePensionProConfiguration } from '../hooks/useUpdatePensionProConfiguration';

type IntegrationProps = {
  applicationId: string;
  connectionId: string;
  onClose: () => void;
};

type Integration = {
  enabled: boolean;
  name: string;
  form: (props: IntegrationProps) => JSX.Element | null;
};

const frequencies = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  bimonthly: 'Bimonthly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
} as const;

function PensionProSettings({
  applicationId,
  connectionId,
  onClose,
}: IntegrationProps) {
  const { connectionConfiguration, isLoading: isConfigLoading } =
    useConnectionConfiguration({
      applicationId,
      connectionId,
      thirdParty: 'pensionPro',
    });
  const [isEnabled, setIsEnabled] = useState(
    connectionConfiguration?.enabled ?? false,
  );
  const employerEin = '123-4567890';
  const [newEin, setNewEin] = useState<string | undefined>(
    connectionConfiguration?.ein_override,
  );
  const toast = useToast();
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isError,
  } = useUpdatePensionProConfiguration({
    applicationId,
    connectionId,
  });
  const onSave = useCallback(
    async (
      values: Pick<PensionProConfiguration, 'enabled' | 'ein_override'>,
    ) => {
      await mutateAsync(values);

      if (!isError) {
        onClose();
        toast({
          title: 'Success',
          description: 'Settings have been successfully updated',
          status: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update settings. Please try again.',
          status: 'error',
        });
      }
    },
    [isError, toast, onClose, mutateAsync],
  );
  const isLoading = isConfigLoading || isSubmitting;

  return (
    <>
      <ModalBody paddingBottom="24px" paddingTop={0}>
        <Stack>
          <Flex dir="row" alignItems={'start'} justifyContent={'space-between'}>
            <Stack gap="4px">
              <Text fontSize={'14px'} fontWeight={500}>
                PensionPro Integration
              </Text>
              <Text fontSize={'14px'} fontWeight={400} color={'#737373'}>
                When turned on, Finch will start syncing ongoing payroll data
                into PensionPro for this employer.
              </Text>
            </Stack>
            <HStack>
              <Text
                fontSize={'14px'}
                fontWeight={500}
                color={isEnabled ? COLORS.GREEN : COLORS.GRAY.GRAY_800}
              >
                {isEnabled ? 'On' : 'Off'}
              </Text>
              <Switch
                isChecked={isEnabled}
                onChange={() => setIsEnabled(!isEnabled)}
              />
            </HStack>
          </Flex>
        </Stack>
      </ModalBody>
      <Divider />
      <ModalBody paddingTop="24px" paddingBottom="0px">
        <Stack gap="8px">
          <Stack gap="4px">
            <Text fontSize={'14px'} fontWeight={500} color={COLORS.FINCH.BLACK}>
              Employer Identification Number (EIN)
            </Text>
            <Text fontSize={'14px'} fontWeight={400} color={'#737373'}>
              Enter the employer&apos;s EIN as registered in PensionPro. If you
              want to override what&apos;s automatically being pulled in by
              Finch, enter in the new EIN below.
            </Text>
          </Stack>
          <Stack>
            <Text fontSize={'14px'} fontWeight={500} color={COLORS.FINCH.BLACK}>
              Provider EIN
            </Text>
            <InputGroup>
              <Input
                disabled={true}
                value={employerEin}
                fontSize={'14px'}
                padding={'12px'}
              />
              <InputRightElement>
                <LockIcon />
              </InputRightElement>
            </InputGroup>
            <Text fontSize={'14px'} fontWeight={500} color={COLORS.FINCH.BLACK}>
              New EIN
            </Text>
            <Input
              value={newEin}
              onChange={(e) => setNewEin(e.target.value)}
              fontSize={'14px'}
              padding={'12px'}
            />
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter w={'100%'} paddingTop="24px">
        <HStack
          w={'100%'}
          gap={2}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          <Button
            onClick={onClose}
            variant="outline"
            border={'1px solid #D9D9D9'}
            _hover={{
              backgroundColor: '#F4F4F4',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                enabled: isEnabled,
                ein_override: newEin,
              })
            }
            isDisabled={isLoading || !newEin || newEin.length === 0}
            disabled={isLoading || !newEin || newEin.length === 0}
            _disabled={{
              backgroundColor: COLORS.GRAY.GRAY_400,
            }}
            backgroundColor={COLORS.FINCH.PURPLE}
            _hover={{
              _disabled: { backgoundColor: COLORS.GRAY.GRAY_400 },
              backgroundColor: '#333dbd',
            }}
            color={COLORS.WHITE}
            alignItems="center"
          >
            Save
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
}

function ReliusSettings({
  applicationId,
  connectionId,
  onClose,
}: IntegrationProps) {
  const toast = useToast();
  const {
    mutateAsync: updateReliusConfiguration,
    isLoading: isSubmitting,
    isError,
  } = useUpdateReliusConfiguration({
    applicationId,
    connectionId,
  });
  const { connectionConfiguration, isLoading: isConfigLoading } =
    useConnectionConfiguration({
      applicationId,
      connectionId,
      thirdParty: 'relius',
    });
  const {
    isLoading: isSeqIdsLoading,
    connectionConfiguration: initialSequenceIds,
  } = useReliusSequenceIds({ applicationId, connectionId });
  const [isReliusIntegrationEnabled, setIsReliusIntegrationEnabled] = useState(
    connectionConfiguration?.enabled ?? false,
  );
  const [planId, setPlanId] = useState(connectionConfiguration?.plan_id);
  const [sequenceIds, setSequenceIds] = useState<Record<string, string>>(
    initialSequenceIds?.reduce((seqIds, payGroup) => {
      seqIds[payGroup.pay_group_id] = payGroup.sequence_id;

      return seqIds;
    }, Object.create(null)),
  );
  const [ensureSequenceIdOpen, setEnsureSequenceIdOpen] = useState(false);
  const onSave = async (values: ReliusConfigurationSchema) => {
    await updateReliusConfiguration(values);

    if (!isError) {
      onClose();
    } else {
      toast({
        title: 'Error updating Relius configuration',
        description: 'Please try again',
        status: 'error',
      });
    }
  };
  const isLoading = isSubmitting || isConfigLoading || isSeqIdsLoading;
  const isDisabled = isLoading;

  return (
    <>
      {ensureSequenceIdOpen && (
        <ReliusSequenceIdWarningModal
          isOpen={ensureSequenceIdOpen}
          onClose={() => {
            setEnsureSequenceIdOpen(false);
            onClose();
          }}
          onSave={() => {
            setEnsureSequenceIdOpen(false);
            onSave({
              enabled: isReliusIntegrationEnabled,
              plan_id: planId ?? '',
              sequenceIds: Object.entries(sequenceIds ?? {}).map(
                ([id, value]) => ({
                  pay_group_id: id,
                  frequency:
                    initialSequenceIds?.find((pg) => pg.pay_group_id === id)
                      ?.frequency ?? '',
                  sequence_id: parseInt(value),
                }),
              ),
            });
            onClose();
          }}
        />
      )}
      <ModalBody paddingBottom="24px" paddingTop={0}>
        <Stack>
          <Flex dir="row" alignItems={'start'} justifyContent={'space-between'}>
            <Stack gap="4px">
              <Text fontSize={'14px'} fontWeight={500}>
                Relius Integration
              </Text>
              <Text fontSize={'14px'} fontWeight={400} color={'#737373'}>
                When turned on, Finch will start syncing ongoing payroll data
                into Relius for this employer.
              </Text>
            </Stack>
            <HStack>
              <Text
                fontSize={'14px'}
                fontWeight={500}
                color={
                  isReliusIntegrationEnabled
                    ? COLORS.GREEN
                    : COLORS.GRAY.GRAY_800
                }
              >
                {isReliusIntegrationEnabled ? 'On' : 'Off'}
              </Text>
              <Switch
                isChecked={isReliusIntegrationEnabled}
                isDisabled={isDisabled}
                onChange={() =>
                  setIsReliusIntegrationEnabled(!isReliusIntegrationEnabled)
                }
              />
            </HStack>
          </Flex>
        </Stack>
      </ModalBody>
      <Divider />
      <ModalBody paddingTop="24px" paddingBottom="0px">
        <Stack gap="8px">
          <Stack gap="4px">
            <Text fontSize={'14px'} fontWeight={500} color={COLORS.FINCH.BLACK}>
              Plan ID{' '}
              <Text as="span" fontSize="13px" fontWeight={400}>
                (required)
              </Text>
            </Text>
            <Text fontSize={'14px'} fontWeight={400} color={'#737373'}>
              Input the employer&apos;s Plan ID as registered in Relius.
            </Text>
          </Stack>
          <Stack>
            <Input
              placeholder={'Enter Plan ID'}
              isDisabled={isDisabled}
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              fontSize={'14px'}
              padding={'12px'}
            />
          </Stack>
        </Stack>
        <Stack paddingTop="32px">
          <Stack gap="8px">
            <Stack gap="4px">
              <Text
                fontSize={'14px'}
                fontWeight={500}
                color={COLORS.FINCH.BLACK}
              >
                Sequence IDs{' '}
                <Text as="span" fontSize="13px" fontWeight={400}>
                  (required)
                </Text>
              </Text>
              <Text fontSize={'14px'} fontWeight={400} color={'#737373'}>
                Sequence IDs can be found in the Payroll Data section in Relius.
                Please ensure there is only one of each frequency in Relius.
              </Text>
            </Stack>
            {isLoading ? (
              <Skeleton width="100%" height="40px" borderRadius="6px" />
            ) : (
              initialSequenceIds?.map(
                ({ frequency, pay_group_id, sequence_id }) => {
                  return (
                    <HStack key={pay_group_id}>
                      <Text
                        fontSize={'14px'}
                        fontWeight={500}
                        minWidth={'80px'}
                      >
                        {frequencies[frequency as keyof typeof frequencies] ??
                          startCase(frequency)}
                      </Text>
                      <Input
                        placeholder="Sequence ID"
                        fontSize={'14px'}
                        padding={'12px'}
                        isDisabled={isDisabled}
                        value={sequenceIds?.[pay_group_id] ?? sequence_id}
                        onChange={(e) =>
                          setSequenceIds((prev) => ({
                            ...prev,
                            [pay_group_id]: e.target.value,
                          }))
                        }
                      />
                    </HStack>
                  );
                },
              )
            )}
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter w={'100%'} paddingTop="24px">
        <HStack
          w={'100%'}
          gap={2}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          <Button
            onClick={onClose}
            variant="outline"
            border={'1px solid #D9D9D9'}
            _hover={{
              backgroundColor: '#F4F4F4',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              let payFrequencies = Object.entries(sequenceIds ?? {}).map(
                ([id, value]) => ({
                  pay_group_id: id,
                  frequency:
                    initialSequenceIds?.find((pg) => pg.pay_group_id === id)
                      ?.frequency ?? '',
                  sequence_id: parseInt(value),
                }),
              );

              if (payFrequencies.length === 0) {
                payFrequencies = initialSequenceIds ?? [];
              }

              onSave({
                enabled: isReliusIntegrationEnabled,
                plan_id: planId ?? '',
                sequenceIds: payFrequencies,
              });
              onClose();
            }}
            isDisabled={isDisabled}
            disabled={
              isSubmitting || Object.entries(sequenceIds ?? {}).length === 0
            }
            backgroundColor={COLORS.FINCH.PURPLE}
            _hover={{
              backgroundColor: '#333dbd',
            }}
            color={COLORS.WHITE}
            alignItems="center"
          >
            Save
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
}

function RetirementConfigurationModalBody({
  integrations,
  props,
}: {
  props: IntegrationProps;
  integrations: Integration[];
}) {
  if (integrations.length === 1 && integrations[0]?.form) {
    const Form = integrations[0].form;

    return <Form {...props} />;
  }

  return (
    <>
      <Tabs flexGrow={1}>
        <TabList marginBottom="6" gap="6" paddingX="6">
          {integrations.map((p, i) => (
            <Tab
              key={i}
              fontSize="14px"
              fontWeight="semibold"
              color={COLORS.FINCH.BLACK}
              padding="0 0 12px 0"
            >
              {p.name}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {integrations.map((p, i) => (
            <TabPanel key={i} padding="0">
              {<p.form {...props} />}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
}

export function RetirementConfigurationModal() {
  const { applicationId, connectionId } = useParams<{
    applicationId: string;
    connectionId: string;
  }>();
  const {
    connectionConfiguration: reliusConnectionConfiguration,
    isLoading: isReliusConfigurationLoading,
  } = useConnectionConfiguration({
    applicationId,
    connectionId,
    thirdParty: 'relius',
  });
  const {
    connectionConfiguration: pensionProConnectionConfiguration,
    isLoading: isPensionProConfigurationLoading,
  } = useConnectionConfiguration({
    applicationId,
    connectionId,
    thirdParty: 'pensionPro',
  });
  const integrations = useMemo<Integration[]>(
    () =>
      [
        {
          name: 'PensionPro',
          form: PensionProSettings,
          enabled: pensionProConnectionConfiguration?.capable ?? false,
        },
        {
          name: 'Relius',
          form: ReliusSettings,
          enabled: reliusConnectionConfiguration?.capable ?? false,
        },
      ].filter((i) => i.enabled),
    [pensionProConnectionConfiguration, reliusConnectionConfiguration],
  );
  const [isOpen, setIsOpen] = useState(false);
  const onClick = useCallback(
    () => (integrations.length > 0 ? setIsOpen(true) : null),
    [integrations],
  );
  const onClose = useCallback(() => setIsOpen(false), []);
  const isLoading =
    isPensionProConfigurationLoading || isReliusConfigurationLoading;
  const isDisabled = isLoading || integrations.length === 0;

  return (
    <>
      <Button
        variant={'outline'}
        borderRadius="lg"
        border="1px solid"
        borderColor={COLORS.GRAY.GRAY_400}
        background={COLORS.WHITE}
        padding="8px"
        _active={{ background: 'none', borderColor: COLORS.FINCH.PURPLE }}
        _hover={{ textDecoration: 'unset' }}
        color={COLORS.FINCH.BLACK}
        height="6"
        fontSize="13px"
        textAlign="left"
        isDisabled={isDisabled}
        onClick={onClick}
      >
        Configure Settings
      </Button>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent minW={'520px'} borderRadius="8px">
          <ModalHeader
            fontSize="16px"
            fontWeight="semibold"
            color={COLORS.FINCH.BLACK}
            style={{
              paddingBottom: '24px',
            }}
          >
            Configure Settings
          </ModalHeader>
          <RetirementConfigurationModalBody
            integrations={integrations}
            props={{ applicationId, connectionId, onClose }}
          />
        </ModalContent>
      </Modal>
    </>
  );
}
